import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { getImageUrl, getWebHost } from '../../config';
import { sendContactForm } from '../../actions/contactActions';
import { validate } from '../../helpers/validation';
import { DEVICES, SMART_TVS, REASONS, DEFAULT_RECORD_TYPE } from '../../constants/Contact';
import _ from 'lodash';
import TextInput from '../common/textinput/TextInput';
import Select from '../common/select/Select';
import FormElement from '../common/form/formelement/FormElement';
import Button from '../common/buttons/button/Button';
import Form from '../common/form/Form';
import FormSection from '../common/form/formsection/FormSection';
import { session } from '../../helpers/storage';
import {
	CONTACT_TEXTAREA,
	CONTACT_SELECT_DEFAULT,
	CONTACT_FIELD_ERROR,
	CONTACT_FIELD_NAME,
	CONTACT_FIELD_EMAIL,
	CONTACT_FIELD_QUESTION,
	CONTACT_PLACEHOLDER_NAME,
	CONTACT_PLACEHOLDER_EMAIL,
	CONTACT_FAILED,
	CONTACT_SUCCESS,
	CONTACT_TV_BRAND,
	CONTACT_DEVICE,
	CONTACT_HEADER,
	CONTACT_REASON,
	CONTACT_SUBMIT,
	CONTACT_CATEGORY,
} from '../../constants/Copy';

import './Contact.scss';
import { CANCEL_FEEDBACK } from '../../constants/CancelAccount';

const logo = getImageUrl('/images/vl-logo.svg');
const host = getWebHost();

const initialState = {
	showTVQuestions: false,
	name: { id: 'name', value: '', isValid: null },
	email: { id: 'email', value: '', isValid: null },
	description: { id: 'description', value: '', isValid: null },
	reason: { id: 'reason', value: '', isValid: null },
	specification: { id: 'specification', value: '', isValid: null },
	category: { id: 'category', value: '', reasons: null, isValid: null },
	recordType: { value: DEFAULT_RECORD_TYPE },
	device: { id: 'device', value: '', isValid: null },
	tv_brand: { value: '' },
	showCancelSubscription: false,
};

function getTextInputError(field) {
	return field.isValid === false ? CONTACT_FIELD_ERROR[field.id] : null;
}

function CancelSubscription() {
	/* eslint-disable react/no-danger */
	return (
		<div
			className="contact-cancel-subscription contact-extra-info"
			dangerouslySetInnerHTML={{ __html: CANCEL_FEEDBACK }}
		/>
	);
}

function TVQuestions({ tv_brand, handleChange }) {
	const brandOptions = SMART_TVS.map((tv) => ({
		id: tv,
		title: tv,
	}));

	return (
		<div>
			<FormSection>
				<FormElement label={CONTACT_TV_BRAND}>
					<Select
						aria-label="Merk SmartTV"
						title="Welk merk SmartTV heeft u?"
						onChange={(value) => handleChange('tv_brand', value)}
						value={tv_brand.value}
					>
						{brandOptions.map((brandOpt) => (
							<option key={brandOpt.id} value={brandOpt.id}>
								{brandOpt.title}
							</option>
						))}
					</Select>
				</FormElement>
			</FormSection>
		</div>
	);
}

function getNewCategoryState(value) {
	const reasons = REASONS.find((reason) => reason.name === value)?.values || [];
	return {
		category: {
			id: 'category',
			value,
			reasons,
			isValid: null,
		},
	};
}

function ContactContainer({ contact, sendContactForm }) {
	const [state, setState] = useState(() => {
		const contactData = session.get('contact');
		if (contactData) {
			const parsedData = JSON.parse(contactData);
			try {
				return {
					...initialState,
					...parsedData,
					...getNewCategoryState(parsedData.category.value),
				};
			} catch (err) {
				return initialState;
			}
		}
		return initialState;
	});

	const categoryOptions = REASONS.map((choice) => ({
		id: choice.name,
		title: choice.name,
	}));

	const onChangeCategory = (value) => handleChange('category', value);
	const onChangeReason = (value) => {
		// Find the selected reason object to get its category
		const selectedReason = category?.reasons?.find((reason) => reason.specification === value);
		const reasonCategory = selectedReason?.category || '';

		// Update specification field with the selected value and reason field with the category
		setState((prev) => ({
			...prev,
			specification: { id: 'specification', value, isValid: null },
			reason: { id: 'reason', value: reasonCategory, isValid: null },
		}));
	};
	const onChangeDevice = (value) => updateDevice('device', value);

	useEffect(() => {
		if (contact.success) {
			sessionStorage.removeItem('contact');
		}
	}, [contact.success]);

	const validateField = (event, data) => {
		const field = event ? event.target.name : data.name;
		const value = event ? event.target.value : data.value;
		const isValid = field ? validate[field](value) : false;

		setState((prev) => {
			return field === 'category'
				? {
						...prev,
						[field]: { id: field, value, reasons: prev.category?.reasons, isValid },
				  }
				: {
						...prev,
						[field]: { id: field, value, isValid },
				  };
		});

		return isValid;
	};

	const handleChange = (type, value) => {
		const stateValue =
			type === 'category'
				? value === 'Diverse'
					? {
							[type]: {
								id: type,
								value,
								isValid: null,
							},
							reason: {
								id: 'reason',
								value,
								isValid: null,
							},
							showCancelSubscription: false,
					  }
					: {
							...getNewCategoryState(value),
							showCancelSubscription: value === 'Opzeggen',
					  }
				: {
						[type]: {
							id: type,
							value,
							isValid: null,
						},
				  };

		setState((prev) => {
			const newState = { ...prev, ...stateValue };
			if (sessionStorage) {
				sessionStorage.setItem('contact', JSON.stringify(newState));
			}

			return newState;
		});
	};

	const updateDevice = (type, value) => {
		if (value === 'Smart TV') {
			setState((prev) => ({
				...prev,
				showTVQuestions: true,
				tv_brand: { value: SMART_TVS[0] },
			}));
		} else {
			setState((prev) => ({
				...prev,
				showTVQuestions: false,
				tv_brand: { value: '' },
			}));
		}

		handleChange(type, value);
	};

	const isValid = (data) => {
		const fields = ['email', 'name', 'description', 'category', 'specification', 'device'];
		const invalid = {};

		fields.forEach((field) => {
			const validField = validateField(null, {
				name: field,
				value: data[field].value,
			});
			invalid[field] = !validField;
		});

		return !Object.values(invalid).some((item) => item === true);
	};

	const onFormSubmit = (event) => {
		event.preventDefault();
		const validation = isValid(state);

		if (validation) {
			const data = state;
			delete data.showTVQuestions;
			delete data.category;
			const contactData = _.mapValues(data, (value) => {
				return value.value ? value.value : value.length > 0 ? value : null;
			});

			sendContactForm(contactData);
		} else {
			setState((prev) => ({ ...prev, error: validation }));
		}
	};

	const {
		description,
		showTVQuestions,
		name,
		email,
		device,
		category,
		specification,
		showCancelSubscription,
		tv_brand,
	} = state;

	return (
		<div className="contact-container">
			<a href={host}>
				<img src={logo} className="contact-logo" />
			</a>
			{contact.success ? (
				<div className="contact-success">
					<h1>{CONTACT_SUCCESS}</h1>
				</div>
			) : (
				<>
					{contact.error && (
						<p className="contact-error" key={contact.timestamp}>
							<span className="contact-error__icon">!</span>
							{CONTACT_FAILED}
						</p>
					)}
					<Form method="post" onSubmit={onFormSubmit} header={CONTACT_HEADER} noValidate>
						<FormSection>
							<FormElement>
								<TextInput
									name="name"
									isValid={name.isValid}
									error={getTextInputError(name)}
									label={CONTACT_FIELD_NAME}
									title={CONTACT_FIELD_NAME}
									placeholder={CONTACT_PLACEHOLDER_NAME}
									onBlur={validateField}
									onChange={(evt) => handleChange('name', evt.target.value)}
									value={name.value}
								/>
							</FormElement>
							<FormElement>
								<TextInput
									name="email"
									isValid={email.isValid}
									error={getTextInputError(email)}
									label={CONTACT_FIELD_EMAIL}
									title={CONTACT_FIELD_EMAIL}
									placeholder={CONTACT_PLACEHOLDER_EMAIL}
									onBlur={validateField}
									onChange={(evt) => handleChange('email', evt.target.value)}
									value={email.value}
								/>
							</FormElement>
						</FormSection>

						<FormSection>
							<FormElement label={CONTACT_CATEGORY} error={getTextInputError(category)}>
								<Select
									aria-label="Het onderwerp"
									title="Het onderwerp"
									onChange={onChangeCategory}
									value={category?.value}
								>
									<option key="category" value="">
										{CONTACT_SELECT_DEFAULT}
									</option>
									{categoryOptions.map((category, number) => (
										<option key={`${category.id}-${number}`} value={category.id}>
											{' '}
											{category.title}{' '}
										</option>
									))}
								</Select>
							</FormElement>
							{showCancelSubscription && <CancelSubscription />}
						</FormSection>
						{category?.reasons && (
							<FormSection>
								<FormElement label={CONTACT_REASON} error={getTextInputError(specification)}>
									<Select
										aria-label="De reden voor contact"
										title="De reden voor contact"
										onChange={onChangeReason}
										value={specification.value}
									>
										<option key="specification" value="">
											{CONTACT_SELECT_DEFAULT}
										</option>
										{category.reasons.map((reason, number) => (
											<option key={`reason-${number}`} value={reason.specification}>
												{' '}
												{reason.displayLabel || reason.specification}{' '}
											</option>
										))}
									</Select>
								</FormElement>
							</FormSection>
						)}
						<FormSection>
							<FormElement label={CONTACT_DEVICE} error={getTextInputError(device)}>
								<Select
									aria-label="Apparaat"
									title="Wat voor een apparaat gebruik je?"
									onChange={onChangeDevice}
									value={device.value}
								>
									<option key="device" value="">
										{CONTACT_SELECT_DEFAULT}
									</option>
									{DEVICES.map((device) => (
										<option key={device} value={device}>
											{device}
										</option>
									))}
								</Select>
							</FormElement>
						</FormSection>
						{showTVQuestions && <TVQuestions tv_brand={tv_brand} handleChange={handleChange} />}
						<FormSection>
							<FormElement label={CONTACT_FIELD_QUESTION}>
								<TextInput
									name="description"
									isTextArea
									error={getTextInputError(description)}
									title={CONTACT_FIELD_QUESTION}
									placeholder={CONTACT_TEXTAREA}
									rows="4"
									cols="50"
									isValid={getTextInputError('description')}
									onChange={(evt) => handleChange('description', evt.target.value)}
									onBlur={validateField}
									value={description.value}
								/>
							</FormElement>
						</FormSection>
						<Button type="submit">{CONTACT_SUBMIT}</Button>
					</Form>
				</>
			)}
		</div>
	);
}

export default connect(
	(state) => ({
		contact: state.contact,
	}),
	{ sendContactForm }
)(ContactContainer);
